import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { SelectButton } from 'primereact/selectbutton';
import PageHeader from '../../components/ui/PageHeader';
import DialogHeader from '../../components/ui/DialogHeader';
import { filtrarDocumento, maxLengthDocumento, placeholderDocumento } from '../../utils/documentoUtils';
import { useAuth } from '../../context/AuthContext';
import { ClienteService } from '../../service/ClienteService';
import { VendedorService } from '../../service/VendedorService';
import { LoteService } from '../../service/LoteService';
import { ReniecService } from '../../service/ReniecService';
import { ContratoService } from '../../service/ContratoService';
import { CotizacionService } from '../../service/CotizacionService';
import { UbigeoService } from '../../service/UbigeoService';
import { InteresadoService } from '../../service/InteresadoService';

import './Contrato.css';

const Contrato = ({ embedded = false }) => {
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const toast = useRef(null);

    // ==========================================
    // ESTADOS DEL FORMULARIO
    // ==========================================
    const [dni, setDni] = useState('');
    const [criterioBusqueda, setCriterioBusqueda] = useState('');
    const [cotizaciones, setCotizaciones] = useState([]);
    const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
    const [cliente, setCliente] = useState(null);
    const [clientes, setClientes] = useState([]);
    const [interesados, setInteresados] = useState([]);
    const [coCompradorSeleccionado, setCoCompradorSeleccionado] = useState(null);
    const [departamentos, setDepartamentos] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [distritos, setDistritos] = useState([]);

    // Modales de Edición (Cliente / Co-Comprador)
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editModalType, setEditModalType] = useState('CLIENTE');
    const [modalData, setModalData] = useState({});
    const [provinciasModal, setProvinciasModal] = useState([]);
    const [distritosModal, setDistritosModal] = useState([]);

    const estadoCivilOptions = [
        { label: 'Soltero(a)', value: 'Soltero(a)' },
        { label: 'Casado(a)', value: 'Casado(a)' },
        { label: 'Divorciado(a)', value: 'Divorciado(a)' },
        { label: 'Viudo(a)', value: 'Viudo(a)' },
        { label: 'Conviviente', value: 'Conviviente' }
    ];

    // Selectores
    const [lotes, setLotes] = useState([]);
    const [loteSeleccionado, setLoteSeleccionado] = useState(null);
    const [urbanizacionSeleccionada, setUrbanizacionSeleccionada] = useState(null);
    const [etapaSeleccionada, setEtapaSeleccionada] = useState(null);
    const [manzanaSeleccionada, setManzanaSeleccionada] = useState(null);
    const [vendedores, setVendedores] = useState([]);
    const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
    const [pendingLoteId, setPendingLoteId] = useState(null);
    const [pendingVendedorId, setPendingVendedorId] = useState(null);

    // Candado de Edición
    const [isEditFinanciamiento, setIsEditFinanciamiento] = useState(false);

    // Parámetros de Financiamiento
    const [lotePrecio, setLotePrecio] = useState(0);
    const [inicialAcordada, setInicialAcordada] = useState(500);
    const [abonoReal, setAbonoReal] = useState(0);
    const [fechaLimiteInicial, setFechaLimiteInicial] = useState(new Date(new Date().setDate(new Date().getDate() + 15)));

    const [cuotas, setCuotas] = useState(36);
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)));

    const [isFlexible, setIsFlexible] = useState(false);
    const [bloquesFlexibles, setBloquesFlexibles] = useState([{ cantidad: 0, monto: 0 }]);

    const [tipoInicial, setTipoInicial] = useState('PARCIAL');
    const [cotizacionOrigenId, setCotizacionOrigenId] = useState(null);

    // Resultados y UI
    const [cronograma, setCronograma] = useState([]);
    const [descripcionGenerada, setDescripcionGenerada] = useState('');

    // Utilidades de Fechas
    const parseLocalYMD = (dateString) => {
        if (!dateString) return null;
        if (dateString.includes('T')) return new Date(dateString);
        const [y, m, d] = dateString.split('-');
        return new Date(y, m - 1, d);
    };

    const getLocalYMD = (dateObj) => {
        if (!dateObj) return null;
        const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
        if (Number.isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Cálculos reactivos
    const montoInicialEfectivo = tipoInicial === 'CERO' ? 0 : inicialAcordada;
    const abonoEfectivo = tipoInicial === 'TOTAL' ? inicialAcordada : tipoInicial === 'CERO' ? 0 : abonoReal;
    const esSeparacion = tipoInicial === 'PARCIAL';
    const inicialCompletaParcial = tipoInicial === 'PARCIAL' && abonoReal >= inicialAcordada && inicialAcordada > 0;
    const faltaPagarInicial = Math.max(montoInicialEfectivo - abonoEfectivo, 0);
    const saldoAFinanciar = Math.max(lotePrecio - montoInicialEfectivo, 0);
    const hasCliente = Boolean(cliente?.numeroDocumento || cliente?.nombres);
    const cuotaPromedio = useMemo(() => {
        if (!Array.isArray(cronograma) || cronograma.length === 0) return 0;
        const cuotasMensuales = cronograma.filter((item) => item?.numero !== 0);
        if (cuotasMensuales.length === 0) return 0;
        const total = cuotasMensuales.reduce((acc, item) => acc + (Number(item?.montoTotal) || 0), 0);
        return total / cuotasMensuales.length;
    }, [cronograma]);

    const bgCuotaCero = tipoInicial === 'CERO' ? 'bg-blue-50 border-blue-200'
        : tipoInicial === 'PARCIAL' ? 'bg-orange-50 border-orange-300'
            : 'bg-green-50 border-green-300';

    const isCotizacionConvertida = cotizacionSeleccionada?.estado === 'CONTRATO' || cotizacionSeleccionada?.estado === 'CONVERTIDO' || cotizacionSeleccionada?.estado === 'CONVERTIDA_A_CONTRATO' || cotizacionSeleccionada?.estado === 'ACTIVO';

    // ==========================================
    // EFECTOS INICIALES
    // ==========================================
    useEffect(() => {
        cargarDatosBase();
    }, []);

    const cotizacionesOrdenadas = useMemo(() => {
        const base = Array.isArray(cotizaciones) ? [...cotizaciones] : [];
        return base.sort((a, b) => {
            const aDate = new Date(a.fechaCotizacion || a.createdAt || 0).getTime();
            const bDate = new Date(b.fechaCotizacion || b.createdAt || 0).getTime();
            return bDate - aDate;
        });
    }, [cotizaciones]);

    const lotesOptions = useMemo(() =>
        lotes.map((item) => ({
            ...item,
            descripcion: `${item?.manzana?.etapa?.urbanizacion?.nombre || ''} / ${item?.manzana?.etapa?.nombre || ''} / Mz ${item?.manzana?.nombre || ''} - Lote ${item?.numero || ''}`.trim()
        })),
        [lotes]
    );

    const urbanizacionesOptions = useMemo(() => {
        const map = new Map();
        lotesOptions.forEach((item) => {
            const urbanizacion = item?.manzana?.etapa?.urbanizacion;
            if (urbanizacion?.id && !map.has(urbanizacion.id)) {
                map.set(urbanizacion.id, { label: urbanizacion.nombre || 'Sin nombre', value: urbanizacion });
            }
        });
        return Array.from(map.values());
    }, [lotesOptions]);

    const etapasOptions = useMemo(() => {
        if (!urbanizacionSeleccionada?.id) return [];
        const map = new Map();
        lotesOptions.forEach((item) => {
            const etapa = item?.manzana?.etapa;
            const urbanizacion = etapa?.urbanizacion;
            if (urbanizacion?.id === urbanizacionSeleccionada.id && etapa?.id && !map.has(etapa.id)) {
                map.set(etapa.id, { label: etapa.nombre || 'Sin nombre', value: etapa });
            }
        });
        return Array.from(map.values());
    }, [lotesOptions, urbanizacionSeleccionada]);

    const manzanasOptions = useMemo(() => {
        if (!etapaSeleccionada?.id) return [];
        const map = new Map();
        lotesOptions.forEach((item) => {
            const manzana = item?.manzana;
            const etapa = manzana?.etapa;
            if (etapa?.id === etapaSeleccionada.id && manzana?.id && !map.has(manzana.id)) {
                map.set(manzana.id, { label: manzana.nombre || 'Sin nombre', value: manzana });
            }
        });
        return Array.from(map.values());
    }, [lotesOptions, etapaSeleccionada]);

    const lotesFiltradosOptions = useMemo(() => {
        if (!manzanaSeleccionada?.id) return [];
        return lotesOptions.filter((item) => item?.manzana?.id === manzanaSeleccionada.id);
    }, [lotesOptions, manzanaSeleccionada]);


    const vendedoresOptions = useMemo(() =>
        vendedores.map((item) => ({
            ...item,
            nombreCompleto: `${item.nombres || ''} ${item.apellidos || ''}`.trim()
        })),
        [vendedores]
    );

    const coCompradoresOptions = useMemo(() => {
        const titularId = cliente?.id;
        const titularDocumento = (cliente?.numeroDocumento || '').trim();

        return (interesados || [])
            .filter((item) => item?.id && item.id !== titularId)
            .filter((item) => (item?.numeroDocumento || '').trim() !== titularDocumento)
            .map((item) => ({
                ...item,
                nombreCompleto: `${item.nombres || ''} ${item.apellidos || ''}`.trim(),
                detalleDocumento: `${item.tipoDocumento || 'DNI'}: ${item.numeroDocumento || 'N/A'}`
            }));
    }, [interesados, cliente]);

    useEffect(() => {
        if (pendingLoteId && lotesOptions.length > 0) {
            const encontrado = lotesOptions.find((item) => item?.id === pendingLoteId);
            if (encontrado) {
                setLoteSeleccionado(encontrado);
                if (encontrado.precioVenta != null) setLotePrecio(encontrado.precioVenta);
                setPendingLoteId(null);
            }
        }
        if (pendingVendedorId && vendedoresOptions.length > 0) {
            const encontrado = vendedoresOptions.find((item) => item?.id === pendingVendedorId);
            if (encontrado) {
                setVendedorSeleccionado(encontrado);
                setPendingVendedorId(null);
            }
        }
    }, [pendingLoteId, pendingVendedorId, lotesOptions, vendedoresOptions]);

    const mapTextOptions = (items) => (items || []).map((item) => ({ label: item, value: item }));

    const mapDistritoOptions = (items) => (items || []).map((item) => {
        const distrito = item?.distrito || item?.nombreDistrito || item?.name || '';
        const ubigeo = item?.ubigeo || item?.idUbigeo || item?.codigoUbigeo || '';
        return { label: distrito, value: distrito, ubigeo };
    });

    const cargarDepartamentos = async () => {
        try {
            const response = await UbigeoService.listarDepartamentos(axiosInstance);
            setDepartamentos(mapTextOptions(response));
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar departamentos.' });
        }
    };

    const cargarProvincias = async (departamento) => {
        if (!departamento) { setProvincias([]); setDistritos([]); return; }
        try {
            const response = await UbigeoService.listarProvincias(departamento, axiosInstance);
            setProvincias(mapTextOptions(response));
        } catch (error) {
            setProvincias([]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar provincias.' });
        }
    };

    const cargarDistritos = async (departamento, provincia) => {
        if (!departamento || !provincia) { setDistritos([]); return; }
        try {
            const response = await UbigeoService.listarDistritos(departamento, provincia, axiosInstance);
            setDistritos(mapDistritoOptions(response));
        } catch (error) {
            setDistritos([]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar distritos.' });
        }
    };

    const onDepartamentoChange = async (value) => {
        setCliente((prev) => ({ ...(prev || {}), departamento: value, provincia: '', distrito: '', ubigeo: '' }));
        await cargarProvincias(value);
        setDistritos([]);
    };

    const onProvinciaChange = async (value) => {
        setCliente((prev) => ({ ...(prev || {}), provincia: value, distrito: '', ubigeo: '' }));
        await cargarDistritos(cliente?.departamento, value);
    };

    const onDistritoChange = (value) => {
        const distritoSelected = distritos.find((item) => item.value === value);
        setCliente((prev) => ({ ...(prev || {}), distrito: value, ubigeo: distritoSelected?.ubigeo || '' }));
    };

    const cargarDatosBase = async () => {
        try {
            const [resClientes, resInteresados, resLotes, resVendedores] = await Promise.all([
                ClienteService.listar(axiosInstance),
                InteresadoService.listar(axiosInstance),
                LoteService.listar(axiosInstance),
                VendedorService.listar(axiosInstance)
            ]);
            setClientes(resClientes || []);
            setInteresados(resInteresados || []);
            setLotes(resLotes || []);
            setVendedores(resVendedores || []);
            await cargarDepartamentos();
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar datos base.' });
        }
    };

    const onLoteChange = (e) => {
        setLoteSeleccionado(e.value);
        if (e.value) {
            setUrbanizacionSeleccionada(e.value.manzana?.etapa?.urbanizacion || null);
            setEtapaSeleccionada(e.value.manzana?.etapa || null);
            setManzanaSeleccionada(e.value.manzana || null);
        }
        if (e.value && e.value.precioVenta != null) {
            setLotePrecio(e.value.precioVenta);
        }
    };

    const cargarDesdeCotizacion = async (seleccionada, clienteExistente) => {
        if (!seleccionada) return;
        setCotizacionOrigenId(seleccionada?.id || null);
        const interesado = seleccionada.interesado || {};

        const dep = interesado.departamento || clienteExistente?.departamento || '';
        const prov = interesado.provincia || clienteExistente?.provincia || '';
        const dist = interesado.distrito || clienteExistente?.distrito || '';
        const ubig = interesado.ubigeo || clienteExistente?.ubigeo || '';

        setCliente((prev) => ({
            ...(prev || {}),
            id: clienteExistente?.id || undefined,
            interesadoId: interesado.id || undefined,
            numeroDocumento: interesado.numeroDocumento || dni,
            tipoDocumento: interesado.tipoDocumento || clienteExistente?.tipoDocumento || 'DNI',
            nombres: interesado.nombres || clienteExistente?.nombres || '',
            apellidos: interesado.apellidos || clienteExistente?.apellidos || '',
            estadoCivil: interesado.estadoCivil || clienteExistente?.estadoCivil || '',
            telefono: interesado.telefono || clienteExistente?.telefono || '',
            email: interesado.email || clienteExistente?.email || '',
            departamento: dep,
            provincia: prov,
            distrito: dist,
            ubigeo: ubig,
            direccion: interesado.direccion || clienteExistente?.direccion || ''
        }));
        setCoCompradorSeleccionado(null);
        if (seleccionada.coComprador) {
            setCoCompradorSeleccionado(seleccionada.coComprador);
        } else if (seleccionada.coCompradorId) {
            const coComEncontrado = interesados.find((item) => item.id === seleccionada.coCompradorId);
            if (coComEncontrado) {
                setCoCompradorSeleccionado(coComEncontrado);
            } else {
                try {
                    const coCompradorDesdeBd = await InteresadoService.obtenerPorId(seleccionada.coCompradorId, axiosInstance);
                    if (coCompradorDesdeBd) setCoCompradorSeleccionado(coCompradorDesdeBd);
                } catch (error) {
                    console.error("Error obteniendo co-comprador por ID", error);
                }
            }
        }

        if (dep) {
            await cargarProvincias(dep);
            if (prov) await cargarDistritos(dep, prov);
        }

        if (seleccionada.vendedor?.id) {
            const vendedorEncontrado = vendedoresOptions.find((item) => item?.id === seleccionada.vendedor.id);
            if (vendedorEncontrado) setVendedorSeleccionado(vendedorEncontrado);
            else setPendingVendedorId(seleccionada.vendedor.id);
        }

        let precioVentaCargado = 0;
        if (seleccionada.lote?.id) {
            const loteEncontrado = lotesOptions.find((item) => item?.id === seleccionada.lote.id);
            const precioVentaCotizacion = seleccionada.precioTotal ?? null;
            if (loteEncontrado) {
                setLoteSeleccionado(loteEncontrado);
                setUrbanizacionSeleccionada(loteEncontrado.manzana?.etapa?.urbanizacion || null);
                setEtapaSeleccionada(loteEncontrado.manzana?.etapa || null);
                setManzanaSeleccionada(loteEncontrado.manzana || null);
                precioVentaCargado = precioVentaCotizacion ?? loteEncontrado.precioVenta ?? 0;
                if (precioVentaCargado != null) {
                    setLotePrecio(precioVentaCargado);
                }
            } else {
                setPendingLoteId(seleccionada.lote.id);
                precioVentaCargado = precioVentaCotizacion ?? 0;
                if (precioVentaCargado != null) {
                    setLotePrecio(precioVentaCargado);
                }
            }
        }

        const inicialCargada = seleccionada.montoInicialAcordado ?? 500;
        const cuotasCargadas = seleccionada.cantidadCuotas || 36;
        const tipoCargado = seleccionada.tipoInicial || 'PARCIAL';
        const abonoCargado = seleccionada.montoAbonadoIncial ?? (tipoCargado === 'TOTAL' ? inicialCargada : (inicialCargada * 0.2));
        const flexCargado = !!(seleccionada.cuotasFlexibles || seleccionada.cuotasEspeciales || seleccionada.montoCuotaEspecial || (seleccionada.bloquesFlexibles && seleccionada.bloquesFlexibles.length > 0));
        let bloques = [];
        if (seleccionada.bloquesFlexibles && seleccionada.bloquesFlexibles.length > 0) {
            bloques = seleccionada.bloquesFlexibles;
        } else if (seleccionada.cuotasEspeciales && seleccionada.montoCuotaEspecial) {
            bloques = [{ cantidad: seleccionada.cuotasEspeciales, monto: seleccionada.montoCuotaEspecial }];
        } else {
            bloques = [{ cantidad: 0, monto: 0 }];
        }

        setInicialAcordada(inicialCargada);
        setAbonoReal(abonoCargado);
        setCuotas(cuotasCargadas);
        setTipoInicial(tipoCargado);
        setIsFlexible(flexCargado);
        setBloquesFlexibles(bloques);

        if (seleccionada.fechaInicioPago) setFechaInicio(parseLocalYMD(seleccionada.fechaInicioPago));

        setTimeout(() => {
            simularConDatos(precioVentaCargado, inicialCargada, cuotasCargadas, tipoCargado, flexCargado, bloques);
        }, 300);
    };

    const buscarCliente = async () => {
        const documento = (cliente?.numeroDocumento || dni || '').trim();
        if (documento.length !== 8) {
            toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'DNI inválido.' });
            return;
        }

        try {
            setCotizacionOrigenId(null);
            setIsEditFinanciamiento(false);

            const listadoClientes = Array.isArray(clientes) ? clientes : [];
            const clienteExistente = listadoClientes.find((item) => item.numeroDocumento === documento);

            const cotizaciones = await CotizacionService.buscarPorDni(documento, axiosInstance);
            const listaCotizaciones = Array.isArray(cotizaciones) ? cotizaciones : [];

            if (listaCotizaciones.length > 0) {
                const sorted = [...listaCotizaciones].sort((a, b) => {
                    return new Date(b.fechaCotizacion || b.createdAt || 0).getTime() - new Date(a.fechaCotizacion || a.createdAt || 0).getTime();
                });
                const seleccionada = sorted[0];
                setCotizaciones(listaCotizaciones);
                setCotizacionSeleccionada(seleccionada);
                await cargarDesdeCotizacion(seleccionada, clienteExistente);
                toast.current?.show({ severity: 'success', summary: 'Cotización encontrada', detail: 'Se cargaron los datos de la cotización.' });
                return;
            }

            if (clienteExistente) {
                setCliente(clienteExistente);
                setCoCompradorSeleccionado(null);
                setDni(clienteExistente.numeroDocumento || documento);
                if (clienteExistente.departamento) await cargarProvincias(clienteExistente.departamento);
                if (clienteExistente.provincia) await cargarDistritos(clienteExistente.departamento, clienteExistente.provincia);
                toast.current?.show({ severity: 'success', summary: 'Encontrado', detail: 'Cliente cargado desde el sistema.' });
                return;
            }

            const response = await ReniecService.consultarDNI(documento, axiosInstance);
            if (!response?.success) {
                toast.current?.show({ severity: 'warn', summary: 'RENIEC', detail: response?.message || 'DNI no encontrado.' });
                return;
            }

            const data = response.data || {};
            const nombres = data.nombres || data.nombre || '';
            const apellidoPaterno = data.apellidoPaterno || data.apellido_paterno || '';
            const apellidoMaterno = data.apellidoMaterno || data.apellido_materno || '';
            setCliente({
                id: undefined,
                numeroDocumento: documento,
                tipoDocumento: 'DNI',
                nombres,
                apellidos: `${apellidoPaterno} ${apellidoMaterno}`.trim(),
                estadoCivil: '',
                telefono: '',
                email: '',
                direccion: ''
            });
            setCoCompradorSeleccionado(null);
            setDni(documento);
            toast.current?.show({ severity: 'success', summary: 'Encontrado', detail: 'Cliente cargado desde RENIEC.' });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error en la búsqueda.' });
        }
    };

    const limpiarResultados = () => {
        setCotizaciones([]);
        setCotizacionSeleccionada(null);
        setCotizacionOrigenId(null);
        setCronograma([]);
    };

    const buscarCotizaciones = async () => {
        const criterio = criterioBusqueda.trim();
        if (criterio.length < 4) {
            toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Ingrese al menos 4 caracteres.' });
            return;
        }

        try {
            const response = await CotizacionService.listar(axiosInstance);
            const listado = Array.isArray(response) ? response : [];
            const criterioNormalizado = criterio.toLowerCase();

            const filtrado = listado.filter((item) => {
                const doc = `${item?.interesado?.numeroDocumento || ''}`.toLowerCase();
                const idCotizacion = `${item?.id || ''}`.toLowerCase();
                return doc.includes(criterioNormalizado) || idCotizacion.includes(criterioNormalizado);
            });

            if (filtrado.length === 0) {
                toast.current?.show({ severity: 'warn', summary: 'Sin resultados', detail: 'No se encontraron cotizaciones para este criterio.' });
                limpiarResultados();
                return;
            }

            setCotizaciones(filtrado);
            const seleccion = [...filtrado].sort((a, b) => {
                const aDate = new Date(a.fechaCotizacion || a.createdAt || 0).getTime();
                const bDate = new Date(b.fechaCotizacion || b.createdAt || 0).getTime();
                return bDate - aDate;
            })[0];
            setCotizacionSeleccionada(seleccion);
            await cargarDesdeCotizacion(seleccion, null);
            setCronograma([]);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo buscar la cotización.' });
        }
    };

    // ==========================================
    // LOGICA DE EDICION Y ELIMINACION CLIENTE/CO-COMPRADOR
    // ==========================================
    const confirmarEliminarCoComprador = () => {
        confirmDialog({
            message: '¿Está seguro de quitar el co-comprador de este contrato?',
            header: 'Confirmar Acción',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, quitar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger shadow-2 border-round-xl font-bold text-white',
            accept: () => setCoCompradorSeleccionado(null)
        });
    };

    const guardarModal = () => {
        if (editModalType === 'CLIENTE') {
            setCliente({ ...cliente, ...modalData });
        } else {
            setCoCompradorSeleccionado({ ...coCompradorSeleccionado, ...modalData });
        }
        setDialogVisible(false);
    };

    const abrirModal = async (type) => {
        setEditModalType(type);
        const sourceData = type === 'CLIENTE' ? cliente : coCompradorSeleccionado;
        setModalData({ ...sourceData });

        if (sourceData?.departamento) {
            try {
                const resProv = await UbigeoService.listarProvincias(sourceData.departamento, axiosInstance);
                setProvinciasModal(mapTextOptions(resProv));
                if (sourceData.provincia) {
                    const resDist = await UbigeoService.listarDistritos(sourceData.departamento, sourceData.provincia, axiosInstance);
                    setDistritosModal(mapDistritoOptions(resDist));
                }
            } catch (e) { }
        } else {
            setProvinciasModal([]);
            setDistritosModal([]);
        }
        setDialogVisible(true);
    };

    const onDepartamentoModalChange = async (value) => {
        setModalData((prev) => ({ ...prev, departamento: value, provincia: '', distrito: '', ubigeo: '' }));
        if (!value) { setProvinciasModal([]); setDistritosModal([]); return; }
        try {
            const res = await UbigeoService.listarProvincias(value, axiosInstance);
            setProvinciasModal(mapTextOptions(res));
        } catch (e) { setProvinciasModal([]); }
        setDistritosModal([]);
    };

    const onProvinciaModalChange = async (value) => {
        setModalData((prev) => ({ ...prev, provincia: value, distrito: '', ubigeo: '' }));
        if (!value || !modalData.departamento) { setDistritosModal([]); return; }
        try {
            const res = await UbigeoService.listarDistritos(modalData.departamento, value, axiosInstance);
            setDistritosModal(mapDistritoOptions(res));
        } catch (e) { setDistritosModal([]); }
    };

    const onDistritoModalChange = (value) => {
        const distritoSelected = distritosModal.find((item) => item.value === value);
        setModalData((prev) => ({ ...prev, distrito: value, ubigeo: distritoSelected?.ubigeo || '' }));
    };

    const buscarDniModal = async () => {
        if (modalData.tipoDocumento && modalData.tipoDocumento !== 'DNI') return;
        const doc = (modalData.numeroDocumento || '').trim();
        if (doc.length !== 8) return;

        if (editModalType === 'COCOMPRADOR') {
            const listadoInteresados = Array.isArray(interesados) ? interesados : [];
            const interesadoExistente = listadoInteresados.find((item) => (item?.numeroDocumento || '').trim() === doc);
            if (interesadoExistente) {
                setModalData((prev) => ({
                    ...prev,
                    id: interesadoExistente.id,
                    tipoDocumento: interesadoExistente.tipoDocumento || prev.tipoDocumento || 'DNI',
                    numeroDocumento: interesadoExistente.numeroDocumento || doc,
                    nombres: interesadoExistente.nombres || '',
                    apellidos: interesadoExistente.apellidos || '',
                    telefono: interesadoExistente.telefono || '',
                    email: interesadoExistente.email || '',
                    estadoCivil: interesadoExistente.estadoCivil || '',
                    departamento: interesadoExistente.departamento || '',
                    provincia: interesadoExistente.provincia || '',
                    distrito: interesadoExistente.distrito || '',
                    ubigeo: interesadoExistente.ubigeo || '',
                    direccion: interesadoExistente.direccion || ''
                }));
                toast.current?.show({ severity: 'info', summary: 'Interesado', detail: 'El interesado ya está registrado.' });
                return;
            }
        }
        try {
            const res = await ReniecService.consultarDNI(doc, axiosInstance);
            if (res?.success) {
                const data = res.data || {};
                const nombres = data.nombres || data.nombre || '';
                const apellidos = data.apellidos || `${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim();
                setModalData((prev) => ({ ...prev, nombres, apellidos }));
                toast.current?.show({ severity: 'success', summary: 'RENIEC', detail: 'Datos encontrados.' });
            }
        } catch (e) { }
    };

    // ==========================================
    // SIMULADOR MATEMÁTICO (Incluye Cuota 0)
    // ==========================================
    const simularConDatos = (pPrecio, pInicial, pCuotas, pTipo, pFlex, pBloquesFlexibles) => {
        const iniEf = pTipo === 'CERO' ? 0 : pInicial;
        const saldo = pPrecio - iniEf;
        if (saldo <= 0 || pCuotas <= 0) return;

        let nc = [];
        const abonoEf = pTipo === 'TOTAL' ? pInicial : pTipo === 'CERO' ? 0 : abonoReal;
        const esSep = pTipo === 'PARCIAL';
        const inicialCompletaParcial = pTipo === 'PARCIAL' && abonoReal >= pInicial && pInicial > 0;
        const falta = iniEf - abonoEf;

        if (pTipo !== 'CERO') {
            const fechaCuotaCero = inicialCompletaParcial ? getLocalYMD(new Date()) : getLocalYMD(fechaLimiteInicial);
            const estadoCuotaCero = pTipo === 'PARCIAL'
                ? (inicialCompletaParcial ? 'SEPARADO' : 'PAGADO_PARCIAL')
                : 'PAGADO_TOTAL';
            nc.push({
                numero: 0, tipoCuota: 'INICIAL', montoTotal: iniEf,
                montoPagado: abonoEf, saldoPendiente: Math.max(falta, 0),
                fecha: fechaCuotaCero,
                estado: estadoCuotaCero
            });
        }

        let eCant = 0;
        let eTotalMonto = 0;
        if (pFlex && pBloquesFlexibles && pBloquesFlexibles.length > 0) {
            pBloquesFlexibles.forEach(b => {
                eCant += (b.cantidad || 0);
                eTotalMonto += ((b.cantidad || 0) * (b.monto || 0));
            });
        }
        let nCant = pCuotas;
        let nSaldo = saldo;
        if (pFlex && eCant > 0) {
            nSaldo = saldo - eTotalMonto;
            nCant = pCuotas - eCant;
        }

        let base = nCant > 0 ? nSaldo / nCant : 0;
        // Normalizar fechaInicio a una Date local (evita shifts por parseo ISO/UTC)
        let bd;
        if (fechaInicio instanceof Date) {
            bd = new Date(fechaInicio.getFullYear(), fechaInicio.getMonth(), fechaInicio.getDate());
        } else {
            bd = parseLocalYMD(fechaInicio) || new Date();
            bd = new Date(bd.getFullYear(), bd.getMonth(), bd.getDate());
        }
        let dia = bd.getDate();

        for (let i = 0; i < pCuotas; i++) {
            let m = base;
            let esEspecial = false;

            if (pFlex && pBloquesFlexibles && pBloquesFlexibles.length > 0) {
                let cuotasAcumuladas = 0;
                for (let b of pBloquesFlexibles) {
                    if (i >= cuotasAcumuladas && i < cuotasAcumuladas + (b.cantidad || 0)) {
                        m = b.monto || 0;
                        esEspecial = true;
                        break;
                    }
                    cuotasAcumuladas += (b.cantidad || 0);
                }
            }

            if (!esEspecial && i === pCuotas - 1 && nCant > 0) {
                let montoAcumuladoEspeciales = pFlex && pBloquesFlexibles && pBloquesFlexibles.length > 0 ? eTotalMonto : 0;
                m = saldo - montoAcumuladoEspeciales - (Math.round(base * 100) / 100) * (nCant - 1);
            }
            let dt = new Date(bd.getFullYear(), bd.getMonth() + i, 1);
            dt.setDate(Math.min(dia, new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate()));

            nc.push({
                numero: i + 1,
                tipoCuota: esEspecial ? 'ESPECIAL' : 'MENSUAL',
                montoTotal: Math.round(m * 100) / 100,
                montoPagado: 0,
                saldoPendiente: Math.round(m * 100) / 100,
                fecha: getLocalYMD(dt),
                estado: 'PENDIENTE'
            });
        }

        setCronograma(nc);
        let desc = pTipo === 'CERO' ? 'Sin cuota inicial. ' : `Cuota Inicial de S/ ${iniEf}. `;
        desc += pFlex && eCant > 0
            ? `Fraccionado con cronograma flexible de ${eCant} cuotas especiales y ${nCant} cuotas regulares.`
            : `Fraccionado en ${pCuotas} cuotas regulares.`;
        setDescripcionGenerada(desc);
    };

    const formatFechaLarga = (value) => {
        if (!value) return '';
        const date = value instanceof Date ? value : parseLocalYMD(value);
        if (!date || Number.isNaN(date.getTime())) return '';
        const meses = [
            'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
            'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
        ];
        return `${date.getDate()} DE ${meses[date.getMonth()]} DEL ${date.getFullYear()}`;
    };

    const normalizarCuotaSimulada = (item, index = 0) => {
        const fecha = item?.fechaVencimiento || item?.fecha || item?.vencimiento || null;
        return {
            numero: item?.numeroCuota ?? item?.numero ?? index + 1,
            tipoCuota: item?.tipoCuota || 'MENSUAL',
            montoTotal: Number(item?.montoTotal ?? item?.monto ?? 0),
            montoPagado: Number(item?.montoPagado ?? 0),
            saldoPendiente: Number(item?.saldoPendiente ?? item?.monto ?? 0),
            fecha,
            estado: item?.estado || 'PENDIENTE'
        };
    };

    const simular = async () => {
        if (saldoAFinanciar <= 0 || cuotas <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Revise el saldo y cuotas.' });
            return;
        }

        const abonoEf = tipoInicial === 'TOTAL' ? inicialAcordada : abonoReal;
        const fechaInicioParaEnvio = fechaInicio instanceof Date ? fechaInicio : parseLocalYMD(fechaInicio);
        const simulacionRequest = {
            precioTotal: lotePrecio,
            montoInicial: inicialAcordada,
            cantidadCuotas: cuotas,
            fechaInicioPago: getLocalYMD(fechaInicioParaEnvio),
            cuotasEspeciales: 0,
            montoCuotaEspecial: 0,
            bloquesFlexibles: isFlexible ? bloquesFlexibles : []
        };

        try {
            const respuesta = await ContratoService.simular(simulacionRequest, axiosInstance);

            let arrayRespuesta = null;
            if (Array.isArray(respuesta)) {
                arrayRespuesta = respuesta;
            } else if (respuesta && (respuesta.cronograma || respuesta.proyeccion || respuesta.cuotas)) {
                arrayRespuesta = respuesta.cronograma || respuesta.proyeccion || respuesta.cuotas;
            }

            if (arrayRespuesta) {
                const abonoEf = tipoInicial === 'TOTAL' ? inicialAcordada : abonoReal;
                const fechaCuotaCero = inicialCompletaParcial ? getLocalYMD(new Date()) : getLocalYMD(fechaLimiteInicial);
                const estadoCuotaCero = tipoInicial === 'PARCIAL'
                    ? (inicialCompletaParcial ? 'SEPARADO' : 'PAGADO_PARCIAL')
                    : 'PAGADO_TOTAL';

                const cuotaInicial = {
                    numero: 0,
                    tipoCuota: 'INICIAL',
                    montoTotal: inicialAcordada,
                    montoPagado: abonoEf,
                    saldoPendiente: Math.max(inicialAcordada - abonoEf, 0),
                    fecha: fechaCuotaCero,
                    estado: estadoCuotaCero
                };

                const cuotasNormalizadas = arrayRespuesta.map((item, index) => {
                    const cuotaNormalizada = normalizarCuotaSimulada(item, index);
                    let cantidadEspeciales = 0;
                    if (isFlexible && bloquesFlexibles) {
                        cantidadEspeciales = bloquesFlexibles.reduce((acc, b) => acc + (b.cantidad || 0), 0);
                    }
                    const esEspecial = isFlexible && Number(cuotaNormalizada.numero) <= cantidadEspeciales;
                    return {
                        ...cuotaNormalizada,
                        tipoCuota: esEspecial ? 'ESPECIAL' : 'MENSUAL'
                    };
                });

                setCronograma([cuotaInicial, ...cuotasNormalizadas]);
                setDescripcionGenerada(respuesta.descripcion || respuesta.mensajeSugerencia || 'Simulación completada');
                return;
            }

            toast.current.show({ severity: 'warn', summary: 'Respuesta inesperada', detail: 'El servidor no devolvió los datos esperados.' });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error en simulación', detail: error.response?.data?.message || 'No se pudo simular el cronograma.' });
            simularConDatos(lotePrecio, inicialAcordada, cuotas, tipoInicial, isFlexible, bloquesFlexibles);
        }
    };

    // Habilita edición y fuerza una simulación inmediata para refrescar cronograma con la fecha correcta
    const handleEnableEdit = () => {
        setIsEditFinanciamiento(true);
        // Dar tiempo a React para aplicar cambios de estado/fecha antes de simular
        setTimeout(() => {
            try { simular(); } catch (e) { /* silent */ }
        }, 150);
    };

    const formatDate = (value) => {
        if (!value) return '';
        const date = value instanceof Date ? value : parseLocalYMD(value);
        if (!date || Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('es-PE');
    };

    // ==========================================
    // GUARDADO OFICIAL DE CONTRATO
    // ==========================================
    const guardarContrato = async () => {
        if (!loteSeleccionado?.id || !vendedorSeleccionado?.id || cronograma.length === 0) {
            toast.current.show({ severity: 'error', summary: 'Faltan datos', detail: 'Seleccione el lote, vendedor y genere la simulación.' });
            return;
        }

        try {
            let idClienteFinal = null;
            if (cliente) {
                const clienteExistente = clientes.find(c => c.numeroDocumento === cliente.numeroDocumento);
                if (clienteExistente) {
                    idClienteFinal = clienteExistente.id;
                } else {
                    const nuevoClientePayload = {
                        tipoDocumento: cliente.tipoDocumento || 'DNI',
                        numeroDocumento: cliente.numeroDocumento || dni,
                        nombres: cliente.nombres,
                        apellidos: cliente.apellidos || '',
                        estadoCivil: cliente.estadoCivil || '',
                        telefono: cliente.telefono || '',
                        email: cliente.email || '',
                        departamento: cliente.departamento || '',
                        provincia: cliente.provincia || '',
                        distrito: cliente.distrito || '',
                        ubigeo: cliente.ubigeo || '',
                        direccion: cliente.direccion || ''
                    };
                    const resCliente = await ClienteService.crear(nuevoClientePayload, axiosInstance);
                    idClienteFinal = resCliente?.data?.id || resCliente?.id;
                }
            }

            if (!idClienteFinal) {
                toast.current.show({ severity: 'error', summary: 'Error de ID', detail: 'No se pudo obtener el ID del cliente.' });
                return;
            }

            let idCoCompradorFinal = null;
            if (coCompradorSeleccionado) {
                // Verificar si es un Cliente existente o si vino como Interesado
                const coCompradorCliente = clientes.find(c => c.numeroDocumento === coCompradorSeleccionado.numeroDocumento);

                if (coCompradorCliente) {
                    idCoCompradorFinal = coCompradorCliente.id;
                } else {
                    const nuevoCoCompradorPayload = {
                        tipoDocumento: coCompradorSeleccionado.tipoDocumento || 'DNI',
                        numeroDocumento: coCompradorSeleccionado.numeroDocumento,
                        nombres: coCompradorSeleccionado.nombres,
                        apellidos: coCompradorSeleccionado.apellidos || '',
                        estadoCivil: coCompradorSeleccionado.estadoCivil || '',
                        telefono: coCompradorSeleccionado.telefono || '',
                        email: coCompradorSeleccionado.email || '',
                        departamento: coCompradorSeleccionado.departamento || '',
                        provincia: coCompradorSeleccionado.provincia || '',
                        distrito: coCompradorSeleccionado.distrito || '',
                        ubigeo: coCompradorSeleccionado.ubigeo || '',
                        direccion: coCompradorSeleccionado.direccion || ''
                    };
                    const resCoComprador = await ClienteService.crear(nuevoCoCompradorPayload, axiosInstance);
                    idCoCompradorFinal = resCoComprador?.data?.id || resCoComprador?.id;
                }
            }

            const contratoPayload = {
                loteId: loteSeleccionado.id,
                clienteId: idClienteFinal,
                coCompradorId: idCoCompradorFinal,
                vendedorId: vendedorSeleccionado.id,
                precioTotal: lotePrecio,
                montoInicialAcordado: inicialAcordada,
                abonoInicialReal: abonoEfectivo,
                fechaLimiteInicial: tipoInicial !== 'CERO' ? getLocalYMD(fechaLimiteInicial) : null,
                cantidadCuotas: cuotas,
                fechaInicioPago: getLocalYMD(fechaInicio),
                cuotasEspeciales: 0,
                montoCuotaEspecial: 0,
                bloquesFlexibles: isFlexible ? bloquesFlexibles : [],
                cotizacionId: cotizacionOrigenId,
                tipoInicial: tipoInicial,
                cuotasFlexibles: isFlexible,
                cuotas: cronograma
            };

            const response = await ContratoService.crear(contratoPayload, axiosInstance);
            toast.current.show({ severity: 'success', summary: '¡Éxito!', detail: 'Contrato emitido correctamente.' });

            const idGenerado = response?.id || response?.data?.id;

            if (idGenerado) {
                try {
                    const blob = await ContratoService.generarVistaPreviaPdf(idGenerado, axiosInstance);
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                } catch (previewError) {
                    toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'El contrato se guardó, pero hubo un error abriendo la vista previa.' });
                }
            }

            setTimeout(() => {
                if (idGenerado) navigate(`/detalle_contrato/${idGenerado}`);
            }, 1500);

            setCronograma([]);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Fallo al guardar contrato.' });
        }
    };

    // ==========================================
    // TEMPLATES VISUALES
    // ==========================================
    const loteOptionTemplate = (option) => (
        <div className="flex justify-content-between align-items-center w-full border-bottom-1 surface-border py-2 px-1">
            <div>
                <div className="font-bold text-700 text-lg">{option.descripcion || `Lote ${option.numero}`}</div>
                <div className="text-sm text-500 mt-1"><i className="pi pi-expand mr-1 text-blue-500"></i>{option.area || 0} m²</div>
            </div>
            <div className="text-right">
                <span className="font-bold text-blue-600 block text-lg">S/ {(option.precioVenta || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                <Tag severity="success" value="Disponible" className="text-xs mt-1" />
            </div>
        </div>
    );

    const loteSelectedTemplate = (option, props) => {
        if (option) return <div className="flex align-items-center font-bold text-blue-800"><i className="pi pi-map-marker mr-2"></i>{option.descripcion || `Lote ${option.numero}`}</div>;
        return <span>{props.placeholder}</span>;
    };

    const estadoTemplate = (rowData) => {
        if (rowData.numero === 0) {
            if (rowData.estado === 'PAGADO_PARCIAL' || rowData.estado === 'SEPARACIÓN') {
                return <Tag severity="warning" value="FALTA INICIAL" />;
            }
            if (rowData.estado === 'SEPARADO') {
                return <Tag severity="warning" value="SEPARADO" />;
            }
            return <Tag severity="success" value="PAGADO" />;
        }
        return <span className="status-badge proyectado">Proyectado</span>;
    };

    const montoTemplate = (rowData) => {
        const montoTotal = Number(rowData.montoTotal || 0);
        const saldoPendiente = Number(rowData.saldoPendiente || 0);
        if (rowData.numero === 0) {
            return (
                <div className="flex flex-column text-right">
                    <span className="font-bold text-blue-700">S/ {montoTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    {saldoPendiente > 0 && <span className="text-xs text-orange-600">Debe: S/ {saldoPendiente.toLocaleString()}</span>}
                </div>
            );
        }
        return <span className="font-bold">S/ {montoTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>;
    };

    const tipoTemplate = (rowData) => {
        if (rowData.tipoCuota === 'ESPECIAL') {
            return <span className="text-orange-600 font-bold flex align-items-center"><i className="pi pi-star-fill mr-1 text-xs"></i> ESPECIAL</span>;
        }
        if (rowData.tipoCuota === 'INICIAL') {
            return <span className="text-blue-700 font-bold flex align-items-center"><i className="pi pi-wallet mr-1 text-xs"></i> INICIAL</span>;
        }
        return <span>{rowData.tipoCuota || 'MENSUAL'}</span>;
    };

    // ==========================================
    // RENDER PRINCIPAL
    // ==========================================
    const renderContenido = () => (
        <div className="grid mt-3 align-items-stretch">

            {/* ====== PANEL IZQUIERDO: CONTEXTO ====== */}
            <div className="col-12 xl:col-5 flex">
                <div className="custom-card mb-4 w-full flex flex-column">
                    <div className="card-header">
                        <i className="pi pi-search text-primary"></i>
                        <span className="font-bold text-lg ml-2">Buscador y Contexto</span>
                    </div>

                    <div className="p-fluid mt-3 flex-grow-1 flex flex-column justify-content-between">
                        <div>
                            {/* Buscador */}
                            <label className="font-medium text-sm mb-2 block">Documento del interesado</label>
                            <div className="p-inputgroup mb-4">
                                <InputText value={criterioBusqueda} onChange={(e) => setCriterioBusqueda(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarCotizaciones()} placeholder="Ej: 72384732" />
                                <Button icon="pi pi-search" onClick={buscarCotizaciones} className="btn-primary-custom" />
                                <Button icon="pi pi-times" className="p-button-outlined" onClick={limpiarResultados} />
                            </div>

                            <div className="custom-card mb-4">
                                <div className="card-header">
                                    <i className="pi pi-list text-primary"></i>
                                    <span className="font-bold text-lg ml-2">Cotizaciones encontradas</span>
                                </div>
                                <div className="p-fluid mt-3">
                                    <DataTable
                                        value={cotizacionesOrdenadas}
                                        selectionMode="single"
                                        selection={cotizacionSeleccionada}
                                        onSelectionChange={async (e) => {
                                            setCotizacionSeleccionada(e.value);
                                            await cargarDesdeCotizacion(e.value, null);
                                            setCronograma([]);
                                        }}
                                        dataKey="id"
                                        paginator
                                        rows={5}
                                        emptyMessage="No hay cotizaciones para mostrar."
                                        className="p-datatable-sm custom-table"
                                    >
                                        <Column header="N°" body={(row, options) => options.rowIndex + 1} style={{ width: '3rem' }}></Column>
                                        <Column header="Documento" body={(row) => `${row?.interesado?.tipoDocumento || 'DNI'} ${row?.interesado?.numeroDocumento || ''}`}></Column>
                                        <Column header="Interesado" body={(row) => `${row?.interesado?.nombres || ''} ${row?.interesado?.apellidos || ''}`.trim()}></Column>
                                        <Column header="Fecha" body={(row) => formatDate(row?.fechaCotizacion)}></Column>
                                        <Column header="Estado" body={(row) => row?.estado || 'VIGENTE'}></Column>
                                    </DataTable>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* ====== PANEL DERECHO: FINANCIAMIENTO ====== */}
            {hasCliente && (
                <div className="col-12 xl:col-7 flex">
                    <div className="custom-card mb-4 w-full flex flex-column">
                        <div className="card-header flex justify-content-between align-items-center">
                            <div>
                                <i className={`pi ${!isEditFinanciamiento ? 'pi-file-check' : 'pi-wallet'} text-primary`}></i>
                                <span className="font-bold text-lg ml-2">{!isEditFinanciamiento ? 'Resumen de Contrato' : 'Configuración de Financiamiento'}</span>
                            </div>

                        </div>

                        <div className="p-fluid grid mt-3">
                            {!isEditFinanciamiento ? (
                                <div className="col-12 fade-in">
                                    {/* 1. SECCIÓN DE COMPRADORES */}
                                    <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">
                                        <i className="pi pi-users mr-2 text-primary"></i>Datos de los Compradores
                                    </h4>
                                    <div className="grid mb-4">
                                        <div className="col-12 md:col-6">
                                            <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full">
                                                <span className="text-xs text-500 uppercase font-bold block mb-1">Titular</span>
                                                <span className="text-lg font-bold text-800 block">{cliente?.nombres} {cliente?.apellidos}</span>
                                                <span className="text-sm text-600"><i className="pi pi-id-card mr-1"></i>{cliente?.numeroDocumento}</span>
                                                {cliente?.telefono && <span className="text-sm text-600 block mt-1"><i className="pi pi-phone mr-1"></i>{cliente.telefono}</span>}
                                            </div>
                                        </div>
                                        {coCompradorSeleccionado && (
                                            <div className="col-12 md:col-6">
                                                <div className="p-3 border-round border-1 border-blue-200 bg-blue-50 shadow-1 h-full">
                                                    <span className="text-xs text-blue-600 uppercase font-bold block mb-1">Co-comprador</span>
                                                    <span className="text-lg font-bold text-blue-900 block">{coCompradorSeleccionado.nombres} {coCompradorSeleccionado.apellidos}</span>
                                                    <span className="text-sm text-blue-700"><i className="pi pi-id-card mr-1"></i>{coCompradorSeleccionado.numeroDocumento}</span>
                                                    {coCompradorSeleccionado.telefono && <span className="text-sm text-blue-700 block mt-1"><i className="pi pi-phone mr-1"></i>{coCompradorSeleccionado.telefono}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. INMUEBLE Y ASESOR */}
                                    <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">
                                        <i className="pi pi-map-marker mr-2 text-indigo-500"></i>Detalles del Inmueble y Asesor
                                    </h4>
                                    <div className="grid mb-4">
                                        <div className="col-12 md:col-6">
                                            <div className="bg-green-50 border-1 border-green-200 border-round p-3 mb-4 h-full flex flex-column justify-content-center shadow-1">
                                                <span className="text-xs text-green-600 uppercase font-bold block mb-1">Inmueble Seleccionado</span>
                                                {loteSeleccionado ? (
                                                    <>
                                                        <span className="text-lg font-bold text-green-900 block">{`${loteSeleccionado.manzana?.etapa?.urbanizacion?.nombre || ''}`}</span>
                                                        <span className="text-sm text-green-700 font-bold block mb-2">{`${loteSeleccionado.manzana?.etapa?.nombre || ''} - Mz ${loteSeleccionado.manzana?.nombre || ''} Lt ${loteSeleccionado.numero || ''}`}</span>
                                                        <div className="mt-auto pt-2 flex justify-content-between align-items-center">
                                                            <span className="text-green-800 font-bold bg-green-200 px-2 py-1 border-round text-sm"><i className="pi pi-expand mr-1"></i>Área: {loteSeleccionado.area} m²</span>
                                                            <span className="text-xl font-bold text-green-700">S/ {parseFloat(loteSeleccionado.precioVenta || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-green-600 font-italic">Ningún lote seleccionado</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full">
                                                <span className="text-xs text-500 uppercase font-bold block mb-1">Vendedor Asignado</span>
                                                <span className="text-lg font-bold text-800 block">{vendedorSeleccionado ? `${vendedorSeleccionado.nombres} ${vendedorSeleccionado.apellidos}` : 'No asignado'}</span>
                                                {vendedorSeleccionado?.numeroDocumento && <span className="text-sm text-600 block mt-1"><i className="pi pi-id-card mr-1"></i>{vendedorSeleccionado.numeroDocumento}</span>}
                                                {vendedorSeleccionado?.telefono && <span className="text-sm text-600 block mt-1"><i className="pi pi-phone mr-1"></i>{vendedorSeleccionado.telefono}</span>}
                                                {vendedorSeleccionado?.email && <span className="text-sm text-600 block mt-1"><i className="pi pi-envelope mr-1"></i>{vendedorSeleccionado.email}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 3. FINANCIAMIENTO (Con el botón) */}
                                    <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">
                                        <i className="pi pi-wallet mr-2 text-green-500"></i>Resumen del Financiamiento
                                    </h4>
                                    <div className="bg-blue-50 border-round-xl border-1 border-blue-200 shadow-1 p-4 mb-4">
                                        <div className="grid">
                                            {/* Highlighted core numbers */}
                                            <div className="col-12 md:col-6 mb-3">
                                                <span className="text-xs text-blue-600 uppercase font-bold block mb-1">Saldo a Financiar</span>
                                                <span className="text-3xl font-bold text-blue-800">S/ {saldoAFinanciar.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="col-12 md:col-6 mb-3">
                                                <span className="text-xs text-green-600 uppercase font-bold block mb-1">Promedio por Cuota ({cuotas})</span>
                                                <span className="text-3xl font-bold text-green-700">S/ {cuotaPromedio.toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-sm font-bold text-600">/ mes</span></span>
                                            </div>

                                            {/* Details row */}
                                            <div className="col-12 md:col-3 mb-2">
                                                <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full text-center">
                                                    <span className="text-xs text-700 uppercase font-bold block mb-1">Precio de Venta</span>
                                                    <span className="text-xl font-bold text-800">S/ {parseFloat(lotePrecio || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-3 mb-2">
                                                <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full text-center">
                                                    <span className="text-xs text-700 uppercase font-bold block mb-1">Inicial Acordada</span>
                                                    <span className="text-xl font-bold text-green-700">S/ {parseFloat(inicialAcordada || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-3 mb-2">
                                                <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full text-center">
                                                    <span className="text-xs text-700 uppercase font-bold block mb-1">Plan de Cuotas</span>
                                                    <span className="text-xl font-bold text-800">{cuotas} meses</span>
                                                    {isFlexible && <div className="text-xs text-orange-600 mt-1 font-bold"><i className="pi pi-star-fill text-xs mr-1"></i>Mixtas</div>}
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-3 mb-2">
                                                <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full text-center">
                                                    <span className="text-xs text-700 uppercase font-bold block mb-1">Día de Pago</span>
                                                    <span className="text-xl font-bold text-800">{fechaInicio instanceof Date ? fechaInicio.toLocaleDateString('es-PE', { day: '2-digit' }) : '-'}</span>
                                                </div>
                                            </div>
                                            <div className="col-12 mt-2">
                                                <div className={`p-3 border-round border-1 shadow-1 h-full flex flex-column justify-content-center ${bgCuotaCero}`}>
                                                    <div className="flex justify-content-between align-items-center mb-3 border-bottom-1 surface-border pb-2">
                                                        <span className="text-sm text-800 uppercase font-bold">Estado de Inicial (Cuota 0)</span>
                                                        {tipoInicial === 'PARCIAL' && !inicialCompletaParcial && <Tag severity="warning" value="SEPARACIÓN" />}
                                                        {tipoInicial === 'PARCIAL' && inicialCompletaParcial && <Tag severity="warning" value="SEPARADO" />}
                                                        {tipoInicial === 'TOTAL' && <Tag severity="success" value="INICIAL COMPLETA" />}
                                                    </div>
                                                    {tipoInicial === 'PARCIAL' ? (
                                                        <div className="flex justify-content-between">
                                                            <div>
                                                                <span className="text-xs text-500 uppercase font-bold block mb-1">Abono Hoy</span>
                                                                <span className="text-xl font-bold text-orange-800">S/ {parseFloat(abonoReal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                            </div>
                                                            <div className="text-right">
                                                                <span className="text-xs text-500 uppercase font-bold block mb-1">Fecha limite separacion</span>
                                                                <span className="text-sm font-bold text-orange-700">
                                                                    {fechaLimiteInicial
                                                                        ? new Date(fechaLimiteInicial).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                                        : '-'}
                                                                </span>
                                                            </div>
                                                            {faltaPagarInicial > 0 && (
                                                                <div className="text-right">
                                                                    <span className="text-xs text-500 uppercase font-bold block mb-1">Saldo Pendiente</span>
                                                                    <span className="text-xl font-bold text-red-600">S/ {faltaPagarInicial.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center">
                                                            <i className="pi pi-check-circle text-green-500 text-3xl mb-2"></i>
                                                            <span className="text-xl font-black block text-green-800">Pagado al 100%</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {!isCotizacionConvertida ? (
                                            <div className="mt-4 flex flex-column md:flex-row justify-content-end gap-3 pt-4 border-top-1 border-blue-200">
                                                <Button
                                                    label="Modificar Parámetros"
                                                    icon="pi pi-sliders-v"
                                                    className="p-button-outlined p-button-secondary bg-white w-full md:w-auto p-button-lg"
                                                    onClick={handleEnableEdit}
                                                />
                                                <Button
                                                    label="Generar Contrato"
                                                    icon="pi pi-check-circle"
                                                    className="btn-success-custom p-button-lg shadow-2 font-bold px-5 w-full md:w-auto"
                                                    disabled={cronograma.length === 0}
                                                    onClick={guardarContrato}
                                                />
                                            </div>
                                        ) : (
                                            <div className="mt-4 pt-4 border-top-1 border-blue-200 text-center">
                                                <Tag severity="info" value="Esta cotización ya fue convertida a contrato. Solo se muestra su detalle." className="text-sm px-3 py-2" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Edit Participants */}
                                    {cliente && (
                                        <div className="col-12 fade-in mb-4">
                                            <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">Modificar Compradores</h4>
                                            <div className="grid">
                                                {/* Titular */}
                                                <div className="col-12 md:col-6">
                                                    <div className="p-3 border-round border-1 surface-border bg-white shadow-1 h-full">
                                                        <div className="flex justify-content-between align-items-center mb-2">
                                                            <span className="text-xs text-500 uppercase font-bold block">Titular del Contrato</span>
                                                            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm p-0 m-0 w-2rem h-2rem" tooltip="Editar Titular" onClick={() => abrirModal('CLIENTE')} />
                                                        </div>
                                                        <span className="text-lg font-bold text-800 block">{`${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim() || '-'}</span>
                                                        <span className="text-sm text-600"><i className="pi pi-id-card mr-1"></i>{`${cliente.tipoDocumento || 'DNI'}: ${cliente.numeroDocumento || ''}`}</span>
                                                    </div>
                                                </div>

                                                {/* Co-comprador */}
                                                <div className="col-12 md:col-6">
                                                    {coCompradorSeleccionado ? (
                                                        <div className="p-3 border-round border-1 border-blue-200 bg-blue-50 shadow-1 h-full">
                                                            <div className="flex justify-content-between align-items-center mb-2">
                                                                <span className="text-xs text-blue-600 uppercase font-bold block">Co-comprador</span>
                                                                <div>
                                                                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm text-blue-600 p-0 m-0 w-2rem h-2rem" tooltip="Editar Co-comprador" onClick={() => abrirModal('COCOMPRADOR')} />
                                                                    <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm p-button-danger p-0 m-0 w-2rem h-2rem ml-2" tooltip="Quitar Co-comprador" onClick={confirmarEliminarCoComprador} />
                                                                </div>
                                                            </div>
                                                            <span className="text-lg font-bold text-blue-900 block">{`${coCompradorSeleccionado.nombres || ''} ${coCompradorSeleccionado.apellidos || ''}`.trim() || '-'}</span>
                                                            <span className="text-sm text-blue-700"><i className="pi pi-id-card mr-1"></i>{`${coCompradorSeleccionado.tipoDocumento || 'DNI'}: ${coCompradorSeleccionado.numeroDocumento || ''}`}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="p-3 border-round border-1 border-dashed surface-border bg-white shadow-none h-full flex flex-column justify-content-center">
                                                            <div className="flex justify-content-between align-items-center mb-2">
                                                                <label className="text-xs text-500 uppercase font-bold m-0">Añadir Co-comprador (Opcional)</label>
                                                                <Button label="Nuevo" icon="pi pi-plus" className="p-button-text p-button-sm p-0 m-0 text-blue-600" onClick={() => abrirModal('COCOMPRADOR')} />
                                                            </div>
                                                            <Dropdown
                                                                value={coCompradorSeleccionado}
                                                                options={coCompradoresOptions}
                                                                onChange={(e) => setCoCompradorSeleccionado(e.value)}
                                                                optionLabel="nombreCompleto"
                                                                placeholder="Seleccione o busque"
                                                                showClear
                                                                filter
                                                                filterBy="nombreCompleto,numeroDocumento"
                                                                className="w-full p-inputtext-sm"
                                                                itemTemplate={(option) => (
                                                                    <div className="flex flex-column">
                                                                        <span className="font-medium">{option.nombreCompleto || 'Sin nombre'}</span>
                                                                        <small className="text-500">{option.detalleDocumento}</small>
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Edit Inmueble & Vendedor */}
                                    <div className="col-12 fade-in mb-4">
                                        <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">Modificar Inmueble y Asesor</h4>
                                        <div className="grid">
                                            <div className="col-12 md:col-6">
                                                <label className="text-xs text-500 uppercase font-bold block mb-2">Inmueble Seleccionado</label>
                                                <div className="p-fluid grid mb-2">
                                                    <div className="col-4">
                                                        <Dropdown value={urbanizacionSeleccionada} options={urbanizacionesOptions} onChange={(e) => { setUrbanizacionSeleccionada(e.value); setEtapaSeleccionada(null); setManzanaSeleccionada(null); setLoteSeleccionado(null); }} placeholder="Proyecto" className="p-inputtext-sm w-full" />
                                                    </div>
                                                    <div className="col-4">
                                                        <Dropdown value={etapaSeleccionada} options={etapasOptions} onChange={(e) => { setEtapaSeleccionada(e.value); setManzanaSeleccionada(null); setLoteSeleccionado(null); }} placeholder="Etapa" disabled={!urbanizacionSeleccionada} className="p-inputtext-sm w-full" />
                                                    </div>
                                                    <div className="col-4">
                                                        <Dropdown value={manzanaSeleccionada} options={manzanasOptions} onChange={(e) => { setManzanaSeleccionada(e.value); setLoteSeleccionado(null); }} placeholder="Manzana" disabled={!etapaSeleccionada} className="p-inputtext-sm w-full" />
                                                    </div>
                                                </div>
                                                {!manzanaSeleccionada ? (
                                                    <Dropdown
                                                        value={loteSeleccionado}
                                                        options={lotesOptions}
                                                        onChange={onLoteChange}
                                                        itemTemplate={loteOptionTemplate}
                                                        valueTemplate={loteSelectedTemplate}
                                                        placeholder="Despliegue para seleccionar lote"
                                                        className="w-full bg-white"
                                                        panelStyle={{ minWidth: '320px' }}
                                                        filter
                                                        filterBy="descripcion,numero"
                                                    />
                                                ) : (
                                                    <div className="lotes-list-container custom-scrollbar mt-2">
                                                        {lotesFiltradosOptions.length === 0 ? (
                                                            <div className="p-3 text-center text-500 font-italic">No hay lotes disponibles en esta manzana.</div>
                                                        ) : (
                                                            lotesFiltradosOptions.map((loteOpt) => (
                                                                <div
                                                                    key={loteOpt.id}
                                                                    className={`lote-item-card ${loteSeleccionado?.id === loteOpt.id ? 'selected' : ''}`}
                                                                    onClick={() => onLoteChange({ value: loteOpt })}
                                                                >
                                                                    <div className="lote-item-info">
                                                                        <span className="lote-item-title">Lote {loteOpt.numero}</span>
                                                                        <span className="lote-item-area"><i className="pi pi-expand mr-1"></i>{loteOpt.area || 0} m²</span>
                                                                    </div>
                                                                    <div className="lote-item-price">
                                                                        <span className="price-tag">S/ {(loteOpt.precioVenta || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                                        <i className={`pi ${loteSeleccionado?.id === loteOpt.id ? 'pi-check-circle text-blue-600' : 'pi-circle text-400'} ml-3 text-xl`}></i>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                                {loteSeleccionado && (
                                                    <div className="mt-2 text-right">
                                                        <span className="text-xs text-500 uppercase font-bold mr-2">Área: {loteSeleccionado.area} m²</span>
                                                        <span className="text-sm font-black text-green-700">Oficial: S/ {loteSeleccionado.precioVenta?.toLocaleString()}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <label className="text-xs text-500 uppercase font-bold block mb-2">Vendedor Asignado</label>
                                                <Dropdown
                                                    value={vendedorSeleccionado}
                                                    options={vendedoresOptions}
                                                    onChange={(e) => setVendedorSeleccionado(e.value)}
                                                    optionLabel="nombreCompleto"
                                                    placeholder="Seleccione Vendedor"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 1. Generales (Financiamiento) */}
                                    <div className="col-12 fade-in">
                                        <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">1. Condiciones Generales</h4>
                                        <div className="grid">
                                            <div className="field col-12 md:col-3">
                                                <label className="font-medium text-xs uppercase text-500">Precio a Tratar (S/)</label>
                                                <InputNumber value={lotePrecio} onValueChange={(e) => setLotePrecio(e.value)} mode="currency" currency="PEN" className="font-bold" />
                                            </div>
                                            <div className="field col-12 md:col-3">
                                                <label className="font-medium text-xs uppercase text-500">Inicial Acordada (S/)</label>
                                                <InputNumber value={inicialAcordada} onValueChange={(e) => setInicialAcordada(e.value)} mode="currency" currency="PEN" className="font-bold" />
                                            </div>
                                            <div className="field col-12 md:col-3">
                                                <label className="font-medium text-xs uppercase text-500">N° Cuotas Mensuales</label>
                                                <InputNumber value={cuotas} onValueChange={(e) => setCuotas(e.value)} showButtons min={1} max={120} />
                                            </div>
                                            <div className="field col-12 md:col-3">
                                                <label className="font-medium text-xs uppercase text-500">Día Fijo de Pago</label>
                                                <Calendar value={fechaInicio} onChange={(e) => {
                                                    const d = e.value;
                                                    if (d instanceof Date) setFechaInicio(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                                                    else setFechaInicio(parseLocalYMD(d));
                                                }} dateFormat="dd/mm/yy" showIcon />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Cuotas Especiales */}
                                    <div className="col-12 mb-4 fade-in">
                                        <div className="bg-slate-50 p-3 border-round border-1 surface-border">
                                            <div className="flex align-items-center">
                                                <Checkbox inputId="flexible" checked={isFlexible} onChange={e => setIsFlexible(e.checked)} />
                                                <label htmlFor="flexible" className="ml-2 font-medium text-sm text-700 cursor-pointer">Simulación Especial (Cuotas Mixtas)</label>
                                            </div>
                                            {isFlexible && (
                                                <div className="p-fluid flexible-blocks-container fade-in mt-3">
                                                    <label className="text-xs font-bold text-orange-800 uppercase mb-2 block">Bloques de Cuotas Especiales</label>
                                                    {bloquesFlexibles.map((bloque, index) => (
                                                        <div key={index} className="grid align-items-end mb-2">
                                                            <div className="field col-5 mb-0">
                                                                <label className="text-xs font-bold text-600">N° Cuotas Fijas</label>
                                                                <InputNumber value={bloque.cantidad} onValueChange={(e) => {
                                                                    const nuevos = [...bloquesFlexibles];
                                                                    nuevos[index].cantidad = e.value;
                                                                    setBloquesFlexibles(nuevos);
                                                                }} placeholder="Ej: 3" />
                                                            </div>
                                                            <div className="field col-5 mb-0">
                                                                <label className="text-xs font-bold text-600">Monto Fijo (S/)</label>
                                                                <InputNumber value={bloque.monto} onValueChange={(e) => {
                                                                    const nuevos = [...bloquesFlexibles];
                                                                    nuevos[index].monto = e.value;
                                                                    setBloquesFlexibles(nuevos);
                                                                }} mode="currency" currency="PEN" placeholder="Ej: 1000" />
                                                            </div>
                                                            <div className="field col-2 mb-0">
                                                                <Button icon="pi pi-trash" className="p-button-danger p-button-outlined" onClick={() => {
                                                                    const nuevos = [...bloquesFlexibles];
                                                                    nuevos.splice(index, 1);
                                                                    setBloquesFlexibles(nuevos);
                                                                }} disabled={bloquesFlexibles.length <= 1} type="button" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <Button label="Agregar Bloque" icon="pi pi-plus" className="p-button-text p-button-sm mt-1" onClick={() => {
                                                        setBloquesFlexibles([...bloquesFlexibles, { cantidad: 1, monto: 100 }]);
                                                    }} type="button" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 2. Inicial */}
                                    <div className="col-12 fade-in">
                                        <h4 className="text-sm font-bold text-600 mb-3 border-bottom-1 surface-border pb-2">2. Resolución de Inicial</h4>
                                        <div className={`p-4 border-round border-2 transition-colors transition-duration-300 ${bgCuotaCero}`}>
                                            <div className="flex justify-content-between align-items-center mb-3">
                                                <label className="font-bold text-800 m-0">Cuota 0 — Inicial</label>
                                                {tipoInicial === 'PARCIAL' && !inicialCompletaParcial && <Tag severity="warning" value="SEPARACIÓN" />}
                                                {tipoInicial === 'PARCIAL' && inicialCompletaParcial && <Tag severity="warning" value="SEPARADO" />}
                                                {tipoInicial === 'TOTAL' && <Tag severity="success" value="INICIAL COMPLETA" />}
                                            </div>
                                            <label className="font-medium text-sm block mb-2 text-800"><i className="pi pi-tag mr-2"></i>Seleccione Intención de Compra:</label>
                                            <SelectButton
                                                value={tipoInicial}
                                                onChange={(e) => e.value && setTipoInicial(e.value)}
                                                options={[
                                                    { label: '⚡ Abono Parcial', value: 'PARCIAL' },
                                                    { label: '✅ Pago Total', value: 'TOTAL' }
                                                ]}
                                                className="w-full mb-4"
                                            />

                                            {tipoInicial === 'PARCIAL' && (
                                                <div className="grid fade-in">
                                                    <div className="col-12 md:col-6">
                                                        <label className="font-bold text-xs uppercase text-orange-900">Abono Hoy (S/)</label>
                                                        <InputNumber value={abonoReal} onValueChange={(e) => setAbonoReal(e.value)} mode="currency" currency="PEN" className="input-highlight-orange" />
                                                    </div>
                                                    <div className="col-12 md:col-6">
                                                        <label className="font-bold text-xs uppercase text-orange-900">Fecha limite separacion</label>
                                                        <Calendar value={fechaLimiteInicial} onChange={(e) => setFechaLimiteInicial(e.value)} dateFormat="dd/mm/yy" showIcon className="w-full" />
                                                    </div>
                                                    <div className="col-12 mt-3">
                                                        <div className="bg-orange-50 p-3 border-round-xl border-1 border-orange-200 flex align-items-center justify-content-between shadow-sm fade-in">
                                                            <div className="flex align-items-center">
                                                                <div className="bg-orange-500 border-circle flex align-items-center justify-content-center mr-3" style={{ width: '32px', height: '32px' }}>
                                                                    <i className="pi pi-info-circle text-white text-lg"></i>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-orange-700 text-xs font-bold uppercase line-height-1">Saldo pendiente</span>
                                                                    <span className="text-orange-900 text-xl font-bold">
                                                                        Faltan S/ {faltaPagarInicial.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                    </span>
                                                                    <span className="text-xs text-orange-700 font-bold uppercase mt-1 block">
                                                                        Fecha limite separacion: {fechaLimiteInicial
                                                                            ? new Date(fechaLimiteInicial).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                                            : '-'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <span className="text-orange-300 font-bold text-2xl opacity-50">!</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {tipoInicial === 'TOTAL' && (
                                                <div className="text-center p-3 bg-white border-round border-1 border-green-200 fade-in">
                                                    <i className="pi pi-check-circle text-green-500 text-2xl mb-2"></i>
                                                    <p className="m-0 font-bold text-green-900">Inicial Cubierta al 100%</p>
                                                    <p className="m-0 text-lg font-black text-green-700">S/ {parseFloat(inicialAcordada).toLocaleString()}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 mt-4 flex justify-content-between fade-in">
                                        <Button label="Cerrar Edición" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setIsEditFinanciamiento(false)} />
                                        <Button label="Generar Cronograma" icon="pi pi-calculator" className="btn-primary-custom" onClick={simular} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ====== TABLA DE PROYECCIÓN ====== */}
            {hasCliente && isEditFinanciamiento && (
                <div className="col-12">
                    <div className="custom-card h-full flex flex-column bg-gray-50 border-none">
                        <div className="card-header flex justify-content-between align-items-center mb-4 bg-white p-3 border-round shadow-1">
                            <div>
                                <i className="pi pi-check-square text-green-500 text-xl"></i>
                                <span className="font-bold text-xl ml-2 text-800">Proyección Oficial</span>

                            </div>
                            {cronograma.length > 0 && (
                                <Button label="Emitir Contrato" icon="pi pi-file-edit" className="btn-success-custom p-button-lg shadow-3 border-round-xl font-bold" onClick={guardarContrato} />
                            )}
                        </div>

                        {cronograma.length === 0 ? (
                            <div className="flex-grow-1 flex flex-column align-items-center justify-content-center text-400 p-5 bg-white border-round border-1 border-gray-200">
                                <i className="pi pi-file-o text-7xl mb-4 opacity-20"></i>
                                <p className="m-0 text-xl font-medium">Contrato no generado</p>
                                <p className="m-0 mt-2">Busque un cliente, configure el financiamiento y simule.</p>
                            </div>
                        ) : (
                            <div className="flex-grow-1 flex flex-column fade-in">
                                {/* Resumen 5 Columnas */}
                                <div className="grid mb-4">
                                    <div className="col-12 md:col-3">
                                        <div className="summary-box bg-white h-full">
                                            <span className="summary-title">Estado Proyectado</span>
                                            <span className={`font-bold ${esSeparacion ? 'text-orange-600' : 'text-green-600'}`}>
                                                {esSeparacion ? 'SEPARADO' : 'ACTIVO'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="summary-box bg-white h-full">
                                            <span className="summary-title text-blue-600">Precio Inmueble</span>
                                            <span className="summary-value text-blue-800">S/ {parseFloat(lotePrecio).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="summary-box bg-white h-full">
                                            <span className="summary-title">Saldo a Financiar</span>
                                            <span className="summary-value">S/ {saldoAFinanciar.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="summary-box bg-white h-full">
                                            <span className="summary-title">Total Cuotas</span>
                                            <span className="summary-value">{cuotas} + Inicial</span>
                                        </div>
                                    </div>
                                    {/* <div className="col-12 md:col-3">
                                        <div className="summary-box bg-white h-full">
                                            <span className="summary-title">Cuota Promedio</span>
                                            <span className="summary-value">S/ {cuotaPromedio.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div> */}
                                </div>

                                <div className="bg-white border-round shadow-1 overflow-hidden">
                                    <DataTable value={cronograma} scrollable scrollHeight="500px" className="p-datatable-sm custom-table"
                                        rowClassName={(data) => ({
                                            'bg-blue-50 font-bold': data.numero === 0,
                                            'bg-orange-50 font-bold': data.tipoCuota === 'ESPECIAL'
                                        })}>
                                        <Column field="numero" header="N°" style={{ width: '8%' }} body={(r) => r.numero === 0 ? '0' : r.numero}></Column>
                                        <Column field="tipoCuota" header="Tipo" style={{ width: '20%' }} body={tipoTemplate}></Column>
                                        <Column field="fecha" header="Vencimiento" style={{ width: '25%' }} body={(row) => <><i className="pi pi-calendar mr-2 text-400"></i>{formatFechaLarga(row.fecha)}</>}></Column>
                                        <Column header="Monto (S/)" body={montoTemplate} style={{ width: '25%', textAlign: 'right' }}></Column>
                                        <Column header="Estado" body={estadoTemplate} style={{ width: '22%', textAlign: 'center' }}></Column>
                                    </DataTable>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="contrato-page cotizacion-page">
            <Toast ref={toast} />
            <ConfirmDialog />
            {!embedded && (
                <PageHeader
                    title="Cierre de Venta"
                    description="Ajuste el financiamiento y genere el contrato oficial."
                    icon="pi pi-check-square"
                />
            )}
            {embedded ? (
                renderContenido()
            ) : (
                <div className="main-content">{renderContenido()}</div>
            )}

            <Dialog
                visible={dialogVisible}
                style={{ width: "800px", maxWidth: "95vw" }}
                header={
                    <DialogHeader
                        title={
                            editModalType === "CLIENTE"
                                ? "Editar Titular"
                                : "Editar Co-comprador"
                        }
                        subtitle="Modifique los datos de contacto y ubicación"
                        icon={editModalType === "CLIENTE" ? "pi pi-user" : "pi pi-users"}
                    />
                }
                modal
                className="p-fluid custom-profile-dialog"
                footer={
                    <div className="dialog-footer-buttons">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary"
                            onClick={() => setDialogVisible(false)}
                        />
                        <Button
                            label="Actualizar"
                            icon="pi pi-check"
                            onClick={guardarModal}
                            autoFocus
                        />
                    </div>
                }
                onHide={() => setDialogVisible(false)}
            >
                <div className="formgrid grid dialog-content-specific pt-2">
                    {/* Tipo de documento */}
                    <div className="field col-12 md:col-6">
                        <label>Tipo Documento</label>
                        <Dropdown
                            value={modalData.tipoDocumento || "DNI"}
                            options={[
                                { label: "DNI", value: "DNI" },
                                { label: "Carnet de Extranjeria", value: "CE" },
                                { label: "RUC", value: "RUC" },
                            ]}
                            onChange={(e) =>
                                setModalData((prev) => ({
                                    ...prev,
                                    tipoDocumento: e.value,
                                    numeroDocumento: "",
                                }))
                            }
                            placeholder="Seleccione tipo"
                            className="w-full"
                        />
                    </div>

                    {/* N° de documento + RENIEC */}
                    <div className="field col-12 md:col-6">
                        <label>Documento</label>
                        <div className="p-inputgroup">
                            <InputText
                                value={modalData.numeroDocumento || ""}
                                onChange={(e) => {
                                    const val = e.target.value || "";
                                    const filtered = filtrarDocumento(
                                        val,
                                        modalData.tipoDocumento || "DNI",
                                    );
                                    setModalData((prev) => ({
                                        ...prev,
                                        numeroDocumento: filtered,
                                    }));
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        buscarDniModal();
                                    }
                                }}
                                keyfilter="pint"
                                maxLength={maxLengthDocumento(modalData.tipoDocumento)}
                                placeholder={placeholderDocumento(modalData.tipoDocumento)}
                            />
                            <Button
                                icon="pi pi-search"
                                className="p-button-outlined"
                                type="button"
                                onClick={buscarDniModal}
                            />
                        </div>
                    </div>

                    {/* Nombres */}
                    <div className="field col-12 md:col-6">
                        <label>Nombres</label>
                        <InputText
                            value={modalData.nombres || ""}
                            onChange={(e) =>
                                setModalData((prev) => ({ ...prev, nombres: e.target.value }))
                            }
                            placeholder="Ingrese nombres"
                        />
                    </div>

                    {/* Apellidos */}
                    <div className="field col-12 md:col-6">
                        <label>Apellidos</label>
                        <InputText
                            value={modalData.apellidos || ""}
                            onChange={(e) =>
                                setModalData((prev) => ({
                                    ...prev,
                                    apellidos: e.target.value,
                                }))
                            }
                            placeholder="Ingrese apellidos"
                        />
                    </div>

                    {/* Email */}
                    <div className="field col-12 md:col-6">
                        <label>Correo Electrónico</label>
                        <InputText
                            value={modalData.email || ""}
                            onChange={(e) =>
                                setModalData((prev) => ({ ...prev, email: e.target.value }))
                            }
                            placeholder="ejemplo@correo.com"
                        />
                    </div>

                    {/* Teléfono */}
                    <div className="field col-12 md:col-6">
                        <label>Teléfono</label>
                        <InputText
                            value={modalData.telefono || ""}
                            onChange={(e) =>
                                setModalData((prev) => ({
                                    ...prev,
                                    telefono: e.target.value,
                                }))
                            }
                            placeholder="Ingrese teléfono"
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Estado Civil</label>
                        <Dropdown
                            value={modalData.estadoCivil || ""}
                            options={estadoCivilOptions}
                            onChange={(e) =>
                                setModalData((prev) => ({ ...prev, estadoCivil: e.value }))
                            }
                            placeholder="Seleccione estado civil"
                            className="w-full"
                            showClear
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Dirección</label>
                        <InputText
                            value={modalData.direccion || ""}
                            onChange={(e) =>
                                setModalData((prev) => ({
                                    ...prev,
                                    direccion: e.target.value,
                                }))
                            }
                            placeholder="Ingrese dirección"
                        />
                    </div>

                    {/* Departamento */}
                    <div className="field col-12 md:col-4">
                        <label>Departamento</label>
                        <Dropdown
                            value={modalData.departamento || ""}
                            options={departamentos}
                            onChange={(e) => onDepartamentoModalChange(e.value)}
                            placeholder="Seleccione dep."
                            className="w-full"
                            filter
                            showClear
                        />
                    </div>

                    {/* Provincia */}
                    <div className="field col-12 md:col-4">
                        <label>Provincia</label>
                        <Dropdown
                            value={modalData.provincia || ""}
                            options={provinciasModal}
                            onChange={(e) => onProvinciaModalChange(e.value)}
                            placeholder="Seleccione prov."
                            className="w-full"
                            filter
                            showClear
                            disabled={!modalData.departamento}
                        />
                    </div>

                    {/* Distrito */}
                    <div className="field col-12 md:col-4">
                        <label>Distrito</label>
                        <Dropdown
                            value={modalData.distrito || ""}
                            options={distritosModal}
                            onChange={(e) => onDistritoModalChange(e.value)}
                            placeholder="Seleccione dist."
                            className="w-full"
                            filter
                            showClear
                            disabled={!modalData.provincia}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Contrato;